import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CpmButton, CpmCheckbox, CpmFieldLabel } from '../../../components/design-system';
import { useAppDispatch } from '../../../store';
import {
    fetchConfigTemplateStates,
    fetchConfigTemplateTags,
    useConfigTemplateTags,
} from '../../../store/slices/configTemplatesListSlice';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type {
    ConfigTemplateIdentity,
    ConfigTemplateLevel,
} from '../../../types/configTemplate.types';
import { CONFIG_TEMPLATE_COPY, licenceEmptyCopy } from '../configTemplateCopy';
import { licenceKey } from '../utils/licenceLabels';
import { catalogStateName, toStateFilterOptions } from '../utils/stateCatalog';
import PopoverField from './PopoverField';
import SegmentedToggle from './SegmentedToggle';

interface IdentityFieldsProps {
    level: ConfigTemplateLevel;
    identity: ConfigTemplateIdentity;
    states: CpmState[];
    statesStatus?: 'idle' | 'loading' | 'success' | 'error';
    onRetryStates?: () => void;
    onChange: (patch: Partial<ConfigTemplateIdentity>) => void;
    disabled?: boolean;
    showHeading?: boolean;
    showErrors?: boolean;
    nameErrorText?: string;
    headingAction?: ReactNode;
    headingNote?: string;
}

function FieldHelp({ children }: { children: string }) {
    return (
        <p
            className="m-0 text-[11.5px] font-semibold text-[var(--cpm-text-secondary)]"
            style={{ marginTop: 6, lineHeight: 1.45 }}
        >
            {children}
        </p>
    );
}

export default function IdentityFields({
    level,
    identity,
    states,
    statesStatus = 'success',
    onRetryStates,
    onChange,
    disabled = false,
    showHeading = true,
    showErrors = false,
    nameErrorText,
    headingAction,
    headingNote,
}: IdentityFieldsProps) {
    const dispatch = useAppDispatch();
    const knownTags = useConfigTemplateTags(level);
    const [tagQuery, setTagQuery] = useState('');

    useEffect(() => {
        if (knownTags.status === 'idle') void dispatch(fetchConfigTemplateTags({ level }));
    }, [dispatch, knownTags.status, level]);

    const stateOptions = useMemo(() => toStateFilterOptions(states), [states]);
    const visibleStates = identity.allStates
        ? states
        : states.filter((s) => identity.states.includes(catalogStateName(s)));

    const hasStates = identity.allStates || identity.states.length > 0;
    const nameError =
        Boolean(nameErrorText) || (showErrors && !identity.name.trim());
    const nameHelper = nameError
        ? nameErrorText || CONFIG_TEMPLATE_COPY.NAME_REQUIRED_SAVE
        : CONFIG_TEMPLATE_COPY.NAME_HELPER;
    const statesError = showErrors && !hasStates;

    const statesSummary = identity.allStates
        ? CONFIG_TEMPLATE_COPY.ALL_STATES
        : identity.states.length
          ? identity.states.join(', ')
          : CONFIG_TEMPLATE_COPY.PICK_STATES;

    const tagsSummary = identity.tags.length
        ? identity.tags.join(', ')
        : CONFIG_TEMPLATE_COPY.NO_TAGS_YET;

    const licenceSummary = identity.licenceTypes.length
        ? `${identity.licenceTypes.length} selected`
        : CONFIG_TEMPLATE_COPY.NONE_SELECTED;

    const filteredTagOptions = knownTags.tags.filter((tag) =>
        tag.toLowerCase().includes(tagQuery.trim().toLowerCase())
    );
    const canAddNewTag =
        tagQuery.trim().length > 0 &&
        !knownTags.tags.some((tag) => tag.toLowerCase() === tagQuery.trim().toLowerCase());

    const toggleTag = (tag: string) => {
        onChange({
            tags: identity.tags.includes(tag)
                ? identity.tags.filter((item) => item !== tag)
                : [...identity.tags, tag],
        });
    };

    const addNewTag = () => {
        const next = tagQuery.trim();
        if (!next || identity.tags.includes(next)) return;
        onChange({ tags: [...identity.tags, next] });
        setTagQuery('');
    };

    const statesLoading = statesStatus === 'idle' || statesStatus === 'loading';
    const statesFailed = statesStatus === 'error';
    const tagsLoading = knownTags.status === 'idle' || knownTags.status === 'loading';
    const tagsFailed = knownTags.status === 'error';
    const retryStates = onRetryStates ?? (() => void dispatch(fetchConfigTemplateStates()));
    const retryTags = () => void dispatch(fetchConfigTemplateTags({ level }));

    return (
        <div className="flex flex-col" style={{ gap: 20 }}>
            {showHeading ? (
                <>
                    <div className="flex flex-wrap items-baseline" style={{ gap: 10 }}>
                        <span
                            className="text-[15.5px] font-extrabold tracking-[-0.01em]"
                            style={{ color: 'var(--cpm-text-primary)' }}
                        >
                            {CONFIG_TEMPLATE_COPY.THIS_TEMPLATE}
                        </span>
                        <span className="text-[11.5px] font-semibold text-[var(--cpm-text-secondary)]">
                            {CONFIG_TEMPLATE_COPY.THIS_TEMPLATE_SUB}
                        </span>
                        {headingAction ? (
                            <>
                                <div className="flex-1" style={{ minWidth: 12 }} />
                                {headingAction}
                            </>
                        ) : null}
                    </div>
                    {headingNote ? (
                        <div
                            className="text-[11.5px] font-semibold text-[var(--cpm-text-secondary)]"
                            style={{ lineHeight: 1.45, marginTop: -8 }}
                        >
                            {headingNote}
                        </div>
                    ) : null}
                </>
            ) : null}

            <label className="flex flex-col" style={{ gap: 6 }}>
                <CpmFieldLabel required>{CONFIG_TEMPLATE_COPY.NAME}</CpmFieldLabel>
                <input
                    value={identity.name}
                    disabled={disabled}
                    placeholder="Template name"
                    onChange={(event) => onChange({ name: event.target.value })}
                    className="h-[40px] rounded-[10px] border-[1.5px] bg-white px-3 text-[14px] font-semibold text-[var(--cpm-text-primary)] outline-none focus:border-[var(--cpm-brand-purple)] focus:shadow-[var(--cpm-ring-focus)]"
                    style={{
                        borderColor: nameError
                            ? 'var(--cpm-shortfall-border)'
                            : 'var(--cpm-border-strong)',
                    }}
                />
                <p
                    className="m-0 text-[11.5px] font-semibold"
                    style={{
                        marginTop: 6,
                        lineHeight: 1.45,
                        color: nameError
                            ? 'var(--cpm-shortfall-text)'
                            : 'var(--cpm-text-secondary)',
                    }}
                >
                    {nameHelper}
                </p>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 16 }}>
                <div>
                    <CpmFieldLabel required tooltip={CONFIG_TEMPLATE_COPY.STATES_TOOLTIP}>
                        States this template is for
                    </CpmFieldLabel>
                    <div className="mt-[6px]">
                        <PopoverField
                            summary={statesSummary}
                            disabled={disabled}
                            error={statesError}
                            muted={!hasStates}
                        >
                            {() => (
                                <OptionsPanelState
                                    loading={statesLoading}
                                    failed={statesFailed}
                                    loadingLabel={CONFIG_TEMPLATE_COPY.COPY_STATE_OPTIONS_WAIT}
                                    failTitle={CONFIG_TEMPLATE_COPY.LOAD_FAIL_STATES}
                                    onRetry={retryStates}
                                >
                                    <div
                                        className="rounded-[9px]"
                                        style={{
                                            background: 'var(--cpm-purple-tint-13)',
                                            padding: '7px 7px 8px',
                                            marginBottom: 5,
                                        }}
                                    >
                                        <CpmCheckbox
                                            label="All states"
                                            checked={identity.allStates}
                                            onChange={() =>
                                                onChange({
                                                    allStates: !identity.allStates,
                                                    states: [],
                                                })
                                            }
                                        />
                                        <div
                                            className="text-[11px] font-semibold text-[var(--cpm-text-secondary)]"
                                            style={{ marginTop: 4, paddingLeft: 27 }}
                                        >
                                            Every state, now and later.
                                        </div>
                                    </div>
                                    <div
                                        className="flex flex-col overflow-y-auto"
                                        style={{ gap: 2, maxHeight: 194 }}
                                    >
                                        {stateOptions.map((opt) => (
                                            <div
                                                key={opt.value}
                                                className="rounded-[9px] hover:bg-[var(--cpm-purple-tint-3)]"
                                                style={{ padding: '7px 6px' }}
                                            >
                                                <CpmCheckbox
                                                    label={opt.label}
                                                    checked={
                                                        !identity.allStates &&
                                                        identity.states.includes(opt.value)
                                                    }
                                                    disabled={disabled}
                                                    onChange={() =>
                                                        onChange({
                                                            allStates: false,
                                                            states: identity.states.includes(
                                                                opt.value
                                                            )
                                                                ? identity.states.filter(
                                                                      (s) => s !== opt.value
                                                                  )
                                                                : [...identity.states, opt.value],
                                                        })
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </OptionsPanelState>
                            )}
                        </PopoverField>
                    </div>
                    <FieldHelp>{CONFIG_TEMPLATE_COPY.STATES_HELPER}</FieldHelp>
                </div>

                <div>
                    <CpmFieldLabel hint={CONFIG_TEMPLATE_COPY.TAGS_HINT}>
                        {CONFIG_TEMPLATE_COPY.TAGS}
                    </CpmFieldLabel>
                    <div className="mt-[6px]">
                        <PopoverField
                            summary={tagsSummary}
                            disabled={disabled}
                            muted={identity.tags.length === 0}
                            panelWidth={280}
                        >
                            {() => (
                                <OptionsPanelState
                                    loading={tagsLoading}
                                    failed={tagsFailed}
                                    loadingLabel={CONFIG_TEMPLATE_COPY.LOADING_TAGS}
                                    failTitle={CONFIG_TEMPLATE_COPY.LOAD_FAIL_TAGS}
                                    onRetry={retryTags}
                                >
                                    <div className="flex items-center" style={{ gap: 7 }}>
                                        <div className="relative flex-1 min-w-0">
                                            <span
                                                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
                                                style={{ color: 'var(--cpm-text-tertiary)' }}
                                            >
                                                <SearchIcon />
                                            </span>
                                            <input
                                                value={tagQuery}
                                                onChange={(event) =>
                                                    setTagQuery(event.target.value)
                                                }
                                                placeholder="Find a tag, or type a new one"
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                        event.preventDefault();
                                                        if (canAddNewTag) addNewTag();
                                                    }
                                                }}
                                                className="w-full h-[36px] rounded-[9px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white pl-8 pr-2.5 text-[13px] outline-none focus:border-[var(--cpm-brand-purple)]"
                                            />
                                        </div>
                                        <CpmButton
                                            variant="outline"
                                            size="sm"
                                            disabled={!canAddNewTag}
                                            leadingIcon={<PlusIcon />}
                                            onClick={addNewTag}
                                            style={{
                                                color: canAddNewTag
                                                    ? '#111111'
                                                    : 'var(--cpm-text-secondary)',
                                                flex: 'none',
                                            }}
                                        >
                                            {CONFIG_TEMPLATE_COPY.ADD_TAG}
                                        </CpmButton>
                                    </div>
                                    <div
                                        className="flex flex-col overflow-y-auto"
                                        style={{ gap: 2, marginTop: 9, maxHeight: 186 }}
                                    >
                                        {filteredTagOptions.map((tag) => (
                                            <div
                                                key={tag}
                                                className="rounded-[9px] hover:bg-[var(--cpm-purple-tint-3)]"
                                                style={{ padding: '7px 6px' }}
                                            >
                                                <CpmCheckbox
                                                    label={tag}
                                                    checked={identity.tags.includes(tag)}
                                                    onChange={() => toggleTag(tag)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {filteredTagOptions.length === 0 ? (
                                        <p
                                            className="m-0 text-[11.5px] font-semibold text-[var(--cpm-text-secondary)]"
                                            style={{ marginTop: 9, lineHeight: 1.45 }}
                                        >
                                            No matching tags. Type a name and press Add.
                                        </p>
                                    ) : null}
                                </OptionsPanelState>
                            )}
                        </PopoverField>
                    </div>
                    <FieldHelp>{CONFIG_TEMPLATE_COPY.TAGS_HELPER}</FieldHelp>
                </div>
            </div>

            <div style={{ maxWidth: 372 }}>
                <CpmFieldLabel tooltip={CONFIG_TEMPLATE_COPY.LICENCE_TOOLTIP}>
                    {CONFIG_TEMPLATE_COPY.LICENCE_TYPES}
                </CpmFieldLabel>
                <div className="mt-[6px]">
                    <PopoverField
                        summary={licenceSummary}
                        disabled={disabled}
                        muted={identity.licenceTypes.length === 0}
                        panelWidth={320}
                    >
                        {() => (
                            <OptionsPanelState
                                loading={statesLoading}
                                failed={statesFailed}
                                loadingLabel={CONFIG_TEMPLATE_COPY.LOADING_LICENCES}
                                failTitle={CONFIG_TEMPLATE_COPY.LOAD_FAIL_LICENCES}
                                onRetry={retryStates}
                            >
                            {visibleStates.length === 0 ? (
                                <p className="m-0 text-[12px] font-semibold leading-[1.5] text-[var(--cpm-text-secondary)]">
                                    Pick States first. Licence types come from the states this
                                    template is for.
                                </p>
                            ) : (
                                <div
                                    className="flex flex-col overflow-y-auto"
                                    style={{ maxHeight: 268 }}
                                >
                                    {visibleStates.map((state) => {
                                        const stateName = catalogStateName(state);
                                        const types = state.licenseTypes ?? [];
                                        return (
                                            <div key={stateName} style={{ padding: '0 0 10px' }}>
                                                <div
                                                    className="uppercase text-[var(--cpm-brand-purple)]"
                                                    style={{
                                                        fontSize: 10.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.08em',
                                                        paddingBottom: 5,
                                                    }}
                                                >
                                                    {stateName}
                                                </div>
                                                {types.length === 0 ? (
                                                    <p className="m-0 text-[11.5px] font-semibold text-[var(--cpm-text-secondary)]">
                                                        {licenceEmptyCopy(stateName)}
                                                    </p>
                                                ) : (
                                                    types.map((type) => {
                                                        const item = {
                                                            state: stateName,
                                                            code: type.code,
                                                        };
                                                        const checked = identity.licenceTypes.some(
                                                            (row) =>
                                                                licenceKey(row) === licenceKey(item)
                                                        );
                                                        return (
                                                            <div
                                                                key={licenceKey(item)}
                                                                className="rounded-[9px] hover:bg-[var(--cpm-purple-tint-3)]"
                                                                style={{ padding: 6 }}
                                                            >
                                                                <CpmCheckbox
                                                                    label={type.label}
                                                                    checked={checked}
                                                                    onChange={() =>
                                                                        onChange({
                                                                            licenceTypes: checked
                                                                                ? identity.licenceTypes.filter(
                                                                                      (row) =>
                                                                                          licenceKey(
                                                                                              row
                                                                                          ) !==
                                                                                          licenceKey(
                                                                                              item
                                                                                          )
                                                                                  )
                                                                                : [
                                                                                      ...identity.licenceTypes,
                                                                                      item,
                                                                                  ],
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            </OptionsPanelState>
                        )}
                    </PopoverField>
                </div>
                <FieldHelp>{CONFIG_TEMPLATE_COPY.LICENCE_HELPER}</FieldHelp>
            </div>

            <div>
                <CpmFieldLabel tooltip={CONFIG_TEMPLATE_COPY.STATUS_TOOLTIP}>
                    {CONFIG_TEMPLATE_COPY.STATUS}
                </CpmFieldLabel>
                <div className="mt-[8px]">
                    <SegmentedToggle
                        value={identity.activeStatus}
                        options={[
                            { value: 'Active', label: 'Active' },
                            { value: 'Inactive', label: 'Inactive' },
                        ]}
                        disabled={disabled}
                        onChange={(next) => onChange({ activeStatus: next })}
                    />
                </div>
            </div>
        </div>
    );
}

function OptionsPanelState({
    loading,
    failed,
    loadingLabel,
    failTitle,
    onRetry,
    children,
}: {
    loading: boolean;
    failed: boolean;
    loadingLabel: string;
    failTitle: string;
    onRetry?: () => void;
    children: ReactNode;
}) {
    if (loading) {
        return (
            <div className="flex flex-col" style={{ gap: 10, padding: '2px 0' }}>
                <div className="flex items-center" style={{ gap: 8 }}>
                    <span className="cpm-pulse-dot" />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>
                        {loadingLabel}
                    </span>
                </div>
                <span className="cpm-sk" style={{ width: '100%', height: 14 }} />
                <span className="cpm-sk" style={{ width: '78%', height: 14 }} />
                <span className="cpm-sk" style={{ width: '62%', height: 14 }} />
            </div>
        );
    }
    if (failed) {
        return (
            <div className="flex flex-col" style={{ gap: 10, padding: '2px 0' }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--cpm-shortfall-text-med)' }}>
                    {failTitle}
                </div>
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: 1.5,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {CONFIG_TEMPLATE_COPY.OPTIONS_FAIL_BODY}
                </div>
                {onRetry ? (
                    <CpmButton variant="outline" size="sm" onClick={onRetry}>
                        {CONFIG_TEMPLATE_COPY.RETRY}
                    </CpmButton>
                ) : null}
            </div>
        );
    }
    return <>{children}</>;
}

function PlusIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
        >
            <path d="M12 5v14M5 12h14" />
        </svg>
    );
}

function SearchIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
        >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
        </svg>
    );
}
